import { useMemo } from 'react';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { getMarketData } from '@/shared/graphql/explorer/queries';
import { type CandlestickData } from '@/shared/graphql/generated/explorer/graphql';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { aggregateMarketData } from '@/shared/utils/marketData';

export default function useMarketData({
  numberOfDays,
  asset,
  resolution = 'HOURLY',
}: {
  numberOfDays: number;
  asset: ChainflipAsset;
  resolution?: 'DAILY' | 'HOURLY';
}) {
  const { data, isLoading, error } = useGqlQuery(getMarketData, {
    variables: { asset, numberOfDays },
    enabled: asset !== undefined,
  });

  const marketData = useMemo(() => aggregateMarketData(data, resolution), [data, resolution]);

  const latestPrice = useMemo(
    () => data?.marketData.candlesticks.findLast((c): c is CandlestickData => 'close' in c)?.close,
    [data],
  );

  const earliestPrice = useMemo(
    () => data?.marketData.candlesticks.find((c): c is CandlestickData => 'close' in c)?.open,
    [data],
  );

  return {
    marketData,
    isLoading,
    error,
    latestPrice,
    earliestPrice,
  };
}
